import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCreditCard,
  FaHashtag,
  FaBuilding,
  FaPaperclip,
  FaTimes,
  FaFilePdf,
  FaWallet,
} from "react-icons/fa";
import PGService from "../../services/PGService";

const FundRequest = () => {
  const [formData, setFormData] = useState({
    bank_id: "", // stores the selected bank's account_number
    amount: "",
    payment_date: "",
    payment_mode: "",
    payment_ref_id: "",
    remark: "",
  });
  const [wallet, seWallet] = useState();
  const [bankList, setBankList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [dynamicPaymentModes, setDynamicPaymentModes] = useState([]);

  useEffect(() => {
    fetchBankList();
  }, []);

  const fetchBankList = async () => {
    setLoadingBanks(true);
    try {
      const result = await PGService.fetchAllBanks();
      if (result.success && result.data?.status === "SUCCESS") {
        setBankList(result?.data?.data || []);
      } else {
        setBankList([]);
      }
    } catch (error) {
      console.error("Failed to fetch bank list", error);
      Swal.fire("Error", "Could not load bank list", "error");
      setBankList([]);
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleBankChange = (e) => {
    const accountNumber = e.target.value; // this is the bank's account_number
    const selectedBank = bankList.find(
      (bank) => bank.account_number.toString() === accountNumber,
    );
    setFormData((prev) => ({
      ...prev,
      bank_id: accountNumber,
      payment_mode: "", // reset payment mode
    }));

    if (selectedBank && selectedBank.payment_modes) {
      const modes = selectedBank.payment_modes.split(",").map((mode) => {
        const trimmed = mode.trim().toUpperCase();
        return { value: trimmed, label: trimmed };
      });
      setDynamicPaymentModes(modes);
    } else {
      setDynamicPaymentModes([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire("Error", "Only JPG, PNG, or PDF files are allowed", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("Error", "File size should be less than 2MB", "error");
      return;
    }

    setPaymentReceipt(file);
    const previewUrl = URL.createObjectURL(file);
    setReceiptPreview(previewUrl);
  };

  const removeReceipt = () => {
    if (receiptPreview) {
      URL.revokeObjectURL(receiptPreview);
    }
    setPaymentReceipt(null);
    setReceiptPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!wallet) {
      Swal.fire("Error", "Please select wallet", "error");
      return;
    }
    if (!formData.bank_id) {
      Swal.fire("Error", "Please select a bank", "error");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      Swal.fire("Error", "Please enter a valid amount", "error");
      return;
    }
    if (!formData.payment_date) {
      Swal.fire("Error", "Please select payment date", "error");
      return;
    }
    if (!formData.payment_mode) {
      Swal.fire("Error", "Please select payment mode", "error");
      return;
    }
    if (!formData.payment_ref_id.trim()) {
      Swal.fire("Error", "Please enter payment reference ID", "error");
      return;
    }
    if (!paymentReceipt) {
      Swal.fire("Error", "Please upload payment receipt", "error");
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("wallet", wallet);
      payload.append("account_number", formData.bank_id); // API expects account_number
      payload.append("amount", formData.amount);
      payload.append("payment_date", formData.payment_date);
      payload.append("payment_mode", formData.payment_mode);
      payload.append("payment_ref_id", formData.payment_ref_id);
      payload.append("remark", formData.remark);
      payload.append("payment_receipt", paymentReceipt);

      const result = await PGService.fundRequest(payload);
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }
      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          "Cash deposit request submitted successfully",
          "success",
        );
        // Reset form
        setFormData({
          bank_id: "",
          amount: "",
          payment_date: "",
          payment_mode: "",
          payment_ref_id: "",
          remark: "",
        });
        removeReceipt();
        setDynamicPaymentModes([]);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        "Request Failed: " + (error.response?.data || error.message),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-2">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <form onSubmit={handleSubmit}>
            {/* Bank Select */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaWallet className="me-1" /> Wallet
              </label>
              <select
                name="wallet_id"
                className="form-select form-select-lg rounded-pill"
                onChange={(e) => {
                  seWallet(e.target.value);
                }}
                required
              >
                <option value="">Select Wallet</option>

                <option value="MAIN_WALLET">MAIN_WALLET</option>
                <option value="BC_WALLET">BC_WALLET</option>
              </select>
              {wallet === "MAIN_WALLET" && (
                <span className="form-text text-muted">
                  Money will be added to Main Wallet (Recharge, Bills & Utility
                  Payments)
                </span>
              )}
              {wallet === "SETTLEMENT_WALLET" && (
                <span className="form-text text-muted small">
                  Money will be added to BC Wallet (DMT - Money Transfer)
                </span>
              )}
            </div>

            {/* Bank Select */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaBuilding className="me-1" /> Bank
              </label>
              <select
                name="bank_id"
                className="form-select form-select-lg rounded-pill"
                value={formData.bank_id}
                onChange={handleBankChange}
                required
                disabled={loadingBanks}
              >
                <option value="">Select Bank</option>
                {bankList.map((bank) => (
                  <option key={bank.id} value={bank.account_number}>
                    {bank.bank_name} ({bank.account_number})
                  </option>
                ))}
              </select>
              {loadingBanks && (
                <div className="small text-muted mt-1">Loading banks...</div>
              )}
            </div>

            {/* Amount */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Amount (₹)</label>
              <input
                type="number"
                name="amount"
                className="form-control form-control-lg rounded-pill"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleChange}
                required
                min="1"
                step="1"
              />
            </div>

            {/* Payment Date */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaCalendarAlt className="me-1" /> Payment Date
              </label>
              <input
                type="date"
                name="payment_date"
                className="form-control form-control-lg rounded-pill"
                value={formData.payment_date}
                onChange={handleChange}
                required
                max={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Payment Mode - Dynamic based on selected bank */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaCreditCard className="me-1" /> Payment Mode
              </label>
              <select
                name="payment_mode"
                className="form-select form-select-lg rounded-pill"
                value={formData.payment_mode}
                onChange={handleChange}
                required
                disabled={!formData.bank_id || dynamicPaymentModes.length === 0}
              >
                <option value="">Select Payment Mode</option>
                {dynamicPaymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
              {formData.bank_id && dynamicPaymentModes.length === 0 && (
                <div className="small text-warning mt-1">
                  No payment modes available for this bank.
                </div>
              )}
            </div>

            {/* Payment Reference ID */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaHashtag className="me-1" /> Payment Reference ID
              </label>
              <input
                type="text"
                name="payment_ref_id"
                className="form-control form-control-lg rounded-pill"
                placeholder="Transaction ID / Cheque No / UTR"
                value={formData.payment_ref_id}
                onChange={handleChange}
                required
              />
            </div>

            {/* Payment Receipt Upload */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                <FaPaperclip className="me-1" /> Payment Receipt (JPG, PNG, PDF)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                className="form-control"
                onChange={handleFileChange}
                required={!paymentReceipt}
              />
              {receiptPreview && (
                <div className="mt-2 position-relative d-inline-block">
                  {paymentReceipt?.type === "application/pdf" ? (
                    <div className="border rounded p-2 d-flex align-items-center">
                      <FaFilePdf size={30} className="text-danger me-2" />
                      <span>{paymentReceipt.name}</span>
                    </div>
                  ) : (
                    <img
                      src={receiptPreview}
                      alt="Receipt preview"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "100px",
                        objectFit: "cover",
                      }}
                      className="border rounded"
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger rounded-circle position-absolute top-0 end-0 translate-middle"
                    onClick={removeReceipt}
                    style={{
                      width: "24px",
                      height: "24px",
                      padding: 0,
                      fontSize: "12px",
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              <div className="form-text text-muted small">
                Upload a clear image or PDF of the payment receipt/screenshot.
              </div>
            </div>

            {/* Remark */}
            <div className="mb-4">
              <label className="form-label fw-semibold">
                Remark (Optional)
              </label>
              <textarea
                name="remark"
                className="form-control rounded-3"
                rows="2"
                placeholder="Any notes for this request"
                value={formData.remark}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              className="btn btn-success btn-lg w-100 rounded-pill shadow"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />{" "}
                  Submitting...
                </>
              ) : (
                "Submit Fund Request"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FundRequest;
